/**
 * Three patterns for verifying a check, all backed by the same data:
 *
 *  - <YesNoButtons />       inline Yes / No only (currently unused; kept as
 *                           the two-option primitive)
 *  - <YesNoNaButtons />     inline Yes / No / third option. Challenge checks
 *                           use the default "N/A"; Daily Profile passes
 *                           "Not sent" via naLabel.
 *  - <ThreeOptionRow />     stacked, full-width labels (Workout sync)
 *
 * Plus:
 *  - <CheckboxRow />        one-time checkbox (Setup Tasks, Sharing tests)
 *
 * Each component expects a current `status` (or null = unverified) and an
 * `onSelect` callback. Visual selected state is driven by `status`; the
 * caller decides what to do when the user taps "No" (typically: write
 * issue_found + open the Asana form).
 */

import type { VerificationStatus } from "../types";

type Variant = "yes-no" | "yes-no-na" | "three-option";

interface InlineButtonsProps {
  status: VerificationStatus | null;
  onSelect: (status: VerificationStatus) => void;
  /** Disable all buttons (e.g. while the mutation is in flight). */
  disabled?: boolean;
}

function btnClass(active: boolean, kind: "yes" | "no" | "na", disabled: boolean) {
  // whitespace-nowrap keeps a multi-word N/A label ("Not sent") on one line
  // rather than wrapping inside the button on narrow phones.
  const base =
    "px-2.5 py-1.5 text-[11px] rounded-md border font-medium min-w-[44px] whitespace-nowrap transition";
  // Selected states need to be visually distinct so testers can see at a
  // glance which option they picked. N/A previously used zinc-100 as
  // selected which was almost indistinguishable from zinc-50 unselected;
  // now uses zinc-700 inverted so the chosen state is unmistakable.
  const palette =
    kind === "yes"
      ? active
        ? "bg-emerald-100 border-emerald-500 text-emerald-900 font-semibold"
        : "bg-white border-zinc-200 text-emerald-800"
      : kind === "no"
      ? active
        ? "bg-rose-100 border-rose-500 text-rose-900 font-semibold"
        : "bg-white border-zinc-200 text-rose-800"
      : active
        ? "bg-zinc-700 border-zinc-700 text-white font-semibold"
        : "bg-white border-zinc-200 text-zinc-600";
  const state = disabled ? "opacity-60 cursor-not-allowed" : "hover:border-zinc-400 cursor-pointer";
  return `${base} ${palette} ${state}`;
}

export function YesNoButtons({ status, onSelect, disabled }: InlineButtonsProps) {
  return (
    <div className="flex gap-1.5 flex-shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("verified_ok")}
        className={btnClass(status === "verified_ok", "yes", !!disabled)}
      >
        Yes
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("issue_found")}
        className={btnClass(status === "issue_found", "no", !!disabled)}
      >
        No
      </button>
    </div>
  );
}

interface YesNoNaButtonsProps extends InlineButtonsProps {
  /**
   * Override the third-option label. Defaults to "N/A" (challenge checks,
   * meaning "no progress on this challenge today"). Daily Profile passes
   * "Not sent", meaning the tester's platform never provides this metric.
   * Both write `skipped`; the label is what tells them apart on screen.
   */
  naLabel?: string;
}

export function YesNoNaButtons({
  status,
  onSelect,
  disabled,
  naLabel = "N/A",
}: YesNoNaButtonsProps) {
  return (
    <div className="flex gap-1.5 flex-shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("verified_ok")}
        className={btnClass(status === "verified_ok", "yes", !!disabled)}
      >
        Yes
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("issue_found")}
        className={btnClass(status === "issue_found", "no", !!disabled)}
      >
        No
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("skipped")}
        className={btnClass(status === "skipped", "na", !!disabled)}
      >
        {naLabel}
      </button>
    </div>
  );
}

interface ThreeOptionRowProps extends InlineButtonsProps {
  /** Override the N/A label for context, e.g. "N/A · no workouts today" */
  naLabel?: string;
}

export function ThreeOptionRow({
  status,
  onSelect,
  disabled,
  naLabel = "N/A",
}: ThreeOptionRowProps) {
  const cls = (active: boolean, kind: Variant | "yes" | "no" | "na") => {
    const base = "flex-1 px-2.5 py-2 text-[11.5px] rounded-md border font-medium text-center leading-tight transition";
    const palette =
      kind === "yes"
        ? active
          ? "bg-emerald-100 border-emerald-500 text-emerald-900 font-semibold"
          : "bg-white border-emerald-200 text-emerald-900"
        : kind === "no"
        ? active
          ? "bg-rose-100 border-rose-500 text-rose-900 font-semibold"
          : "bg-white border-rose-200 text-rose-900"
        : active
          ? "bg-zinc-700 border-zinc-700 text-white font-semibold"
          : "bg-white border-zinc-300 text-zinc-700";
    const state = disabled
      ? "opacity-60 cursor-not-allowed"
      : "hover:border-zinc-500 cursor-pointer";
    return `${base} ${palette} ${state}`;
  };
  return (
    <div className="flex gap-1.5 px-3 pb-3 pt-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("verified_ok")}
        className={cls(status === "verified_ok", "yes")}
      >
        Yes, all correct
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("issue_found")}
        className={cls(status === "issue_found", "no")}
      >
        No, something's off
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect("skipped")}
        className={cls(status === "skipped", "na")}
      >
        {naLabel}
      </button>
    </div>
  );
}

interface CheckboxRowProps {
  status: VerificationStatus | null;
  onCheck: () => void;
  disabled?: boolean;
}

/** One-tap checkbox for one-time tests (Setup Tasks, Sharing tests). */
export function CheckboxRow({ status, onCheck, disabled }: CheckboxRowProps) {
  const checked = status === "verified_ok";
  return (
    <button
      type="button"
      disabled={disabled || checked}
      onClick={onCheck}
      className={`w-[22px] h-[22px] rounded-md border-[1.5px] flex-shrink-0 relative transition ${
        checked
          ? "bg-emerald-600 border-emerald-600"
          : "bg-white border-zinc-300 hover:border-zinc-400"
      } ${disabled || checked ? "cursor-default" : "cursor-pointer"}`}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && (
        <span
          className="absolute"
          style={{
            left: 7,
            top: 3,
            width: 5,
            height: 10,
            border: "solid white",
            borderWidth: "0 2px 2px 0",
            transform: "rotate(45deg)",
          }}
        />
      )}
    </button>
  );
}
